import { noNameAvailable, personaItemDeletedAtDate } from './displayNameUtils.locstring.json';
import { isNotFavoriteAriaLabel } from 'owa-locstrings/lib/strings/isnotfavoritearialabel.locstring.json';
import { isFavoriteAriaLabel } from 'owa-locstrings/lib/strings/isfavoritearialabel.locstring.json';
import { contactListLabel } from 'owa-locstrings/lib/strings/contactlistlabel.locstring.json';
import { groupLabel } from 'owa-locstrings/lib/strings/grouplabel.locstring.json';
import { publicDistributionListLabel } from 'owa-locstrings/lib/strings/publicdistributionlistlabel.locstring.json';
import loc, { format } from 'owa-localize';
import { Person, HubPersona, PersonType, DeletedPersona } from '../..';
import DisplayOrder from 'owa-people-types/lib/types/DisplayOrder';
import { userDate, formatUserDate } from 'owa-datetime';

/**
 * Returns the full display name of the persona, including prefix and postfix (generation), if defined
 * @param persona The persona
 * @param displayOrder The display order of the name of the persona: first-last or last-first
 */
export function getFullDisplayName(persona: HubPersona, displayOrder: DisplayOrder): string {
    if (
        persona.type === PersonType.FavoritePerson ||
        persona.type === PersonType.FavoritePrivateDistributionList ||
        persona.type === PersonType.FavoriteUnifiedGroup ||
        persona.type === PersonType.Generic
    ) {
        return persona.info.displayName;
    } else if (persona.type === PersonType.Group || persona.type === PersonType.GroupBasicData) {
        return persona.displayName;
    }

    if (displayOrder === DisplayOrder.FirstNameLastName) {
        return persona.type === PersonType.Person
            ? getPersonaDisplayNameFirstLast(persona)
            : persona.info.displayNameFirstLast;
    } else if (displayOrder === DisplayOrder.LastNameFirstName) {
        return persona.type === PersonType.Person
            ? getPersonaDisplayNameLastFirst(persona)
            : persona.info.displayNameLastFirst;
    }

    throw new Error(`Invalid display order ${displayOrder}`);
}

/**
 * Returns the display name for a persona in the first last order: Mr James Bond Jr
 * @param persona The persona
 */
function getPersonaDisplayNameFirstLast(persona: Person): string {
    return persona.info.displayNameFirstLast
        ? [persona.info.prefix, persona.info.displayNameFirstLast, persona.info.generation]
              .join(' ')
              .trim()
        : persona.info.originalDisplayName;
}

/**
 * Returns the display name for a persona in the last first order: Bond James Jr Mr
 * @param persona The persona
 */
function getPersonaDisplayNameLastFirst(persona: Person): string {
    return persona.info.displayNameLastFirst
        ? [persona.info.displayNameLastFirst, persona.info.generation, persona.info.prefix]
              .join(' ')
              .trim()
        : persona.info.originalDisplayName;
}

/**
 * Returns detailed aria label for a Persona.
 * @param name Returns aria label for a persona.
 * @param secondaryTextLabel
 * @param isFavorite
 */
export function getPersonaDetailedAriaLabel(
    name: string,
    secondaryTextLabel: string,
    isFavorite: boolean
): string {
    return (
        name +
        ', ' +
        secondaryTextLabel +
        ', ' +
        (isFavorite ? loc(isFavoriteAriaLabel) : loc(isNotFavoriteAriaLabel))
    );
}

/**
 * Returns the value to display as the primary text
 * @param element An element representing the persona
 * @param displayOrder The display order of the name of the persona: first-last or last-first
 */
export function getPrimaryText(element: HubPersona, displayOrder: DisplayOrder): string {
    const displayName = getFullDisplayName(element, displayOrder);
    return displayName ? displayName : loc(noNameAvailable);
}

/**
 * Returns the value to display for the secondary text.
 * @param element An element representing the persona
 * @param primaryText The primary text displayed. We don't want the secondary text to be the same.
 */
export function getSecondaryText(element: HubPersona, primaryText: string): string {
    if ('deletedDate' in element && (<DeletedPersona>element).deletedDate) {
        return secondaryTextDeletedItem(element);
    }

    if (
        element.type === PersonType.PrivateDistributionList ||
        element.type === PersonType.FavoritePrivateDistributionList
    ) {
        return loc(contactListLabel);
    }

    if (element.type === PersonType.PublicDistributionList) {
        return loc(publicDistributionListLabel);
    }

    if (element.type === PersonType.FavoritePerson) {
        return element.info.mainEmailAddress;
    }

    if (
        element.type === PersonType.Group ||
        element.type === PersonType.GroupBasicData ||
        element.type === PersonType.FavoriteUnifiedGroup
    ) {
        return loc(groupLabel);
    }

    if (element.type === PersonType.Person) {
        if (element.info.mainEmailAddress && element.info.mainEmailAddress !== primaryText) {
            return element.info.mainEmailAddress;
        }

        if (element.info.phoneNumber && element.info.phoneNumber !== primaryText) {
            return element.info.phoneNumber;
        }

        if (element.info.company && element.info.company !== primaryText) {
            return element.info.company;
        }
    }

    if (element.type === PersonType.Generic) {
        if (element.info.location && element.info.location !== primaryText) {
            return element.info.location;
        }

        if (element.info.phoneNumber && element.info.phoneNumber !== primaryText) {
            return element.info.phoneNumber;
        }

        if (element.info.mainEmailAddress && element.info.mainEmailAddress !== primaryText) {
            return element.info.mainEmailAddress;
        }
    }

    return null;
}

/**
 * Return the displayName to use when favoriting. It's either the full name, a fallback or a "Not Available" string
 */
export function getDisplayNameForFavoriting(person: Person): string {
    const fullDisplayName = getFullDisplayName(person, DisplayOrder.FirstNameLastName);
    const personaSecondaryText = getSecondaryText(person, fullDisplayName);

    return fullDisplayName || personaSecondaryText || loc(noNameAvailable);
}

export function getPersonaCoinName(element: HubPersona, displayOrder?: DisplayOrder): string {
    const primaryTextLabel = getPrimaryText(
        element,
        displayOrder ? displayOrder : DisplayOrder.FirstNameLastName
    );
    const possibleName = getHubPersonaDisplayNameFirstLast(element);
    return possibleName ? possibleName : primaryTextLabel;
}

/**
 * Compares if the secontary text will be equal for two two HubPersonas
 * @param element1 An element1 representing the persona
 * @param primaryText1 The primary text of element1 displayed. We don't want the secondary text to be the same.
 * @param element2 An element2 representing the persona
 * @param primaryText2 The primary text of element2 displayed. We don't want the secondary text to be the same.
 */
export function secondaryTextComparer(
    element1: HubPersona,
    primaryText1: string,
    element2: HubPersona,
    primaryText2: string
): boolean {
    return getSecondaryText(element1, primaryText1) === getSecondaryText(element2, primaryText2);
}

/**
 * Returns the value to display for the secondary text for DeleteItems
 * @param element An element representing the persona
 * @param culture Use culture to apply to Date formatting
 */
function secondaryTextDeletedItem(element: DeletedPersona): string {
    const displayedDeletedDate = formatUserDate(userDate(element.deletedDate));

    return element.type === PersonType.PrivateDistributionList
        ? `${loc(contactListLabel)} · ${format(
              loc(personaItemDeletedAtDate),
              displayedDeletedDate
          )}`
        : format(loc(personaItemDeletedAtDate), displayedDeletedDate);
}

export function getHubPersonaDisplayNameFirstLast(persona: HubPersona): string {
    switch (persona.type) {
        case PersonType.PrivateDistributionList:
        case PersonType.PublicDistributionList:
        case PersonType.Person:
            return persona.info.displayNameFirstLast;

        case PersonType.GroupBasicData:
        case PersonType.Group:
            return persona.displayName;

        case PersonType.FavoritePerson:
        case PersonType.FavoritePrivateDistributionList:
        case PersonType.FavoriteUnifiedGroup:
        case PersonType.Generic:
            return persona.info.displayName;
        default:
            throw new Error('HubPersonaType Not supported');
    }
}

export function getHubPersonaDisplayNameLastFirst(persona: HubPersona): string {
    switch (persona.type) {
        case PersonType.PrivateDistributionList:
        case PersonType.PublicDistributionList:
        case PersonType.Person:
            return persona.info.displayNameLastFirst;

        case PersonType.GroupBasicData:
        case PersonType.Group:
            return persona.displayName;

        case PersonType.FavoritePerson:
        case PersonType.FavoritePrivateDistributionList:
        case PersonType.FavoriteUnifiedGroup:
        case PersonType.Generic:
            return persona.info.displayName;
        default:
            throw new Error('HubPersonaType Not supported');
    }
}
